import { Filter } from '../gridFilters/types';
import { GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { AlertsColumnConfiguration } from '@nirvana/shared/dtos/types/alerts/common';

export enum FilterEvents {
  FILTERS_UPDATED = 'filtersUpdated',
  TOGGLE_FILTERS_VISIBILITY = 'toggleFiltersVisibility',
}

export enum GridEvents {
  GRID_STARTED = 'gridStarted',
  GRID_LOADED = 'gridLoaded',
  LOAD_MORE_ROWS = 'loadMoreRows',
  LOAD_FIRST_PAGE = 'loadFirstPage',
  LOAD_LAST_PAGE = 'loadLastPage',
  LOAD_NEXT_PAGE = 'loadNextPage',
  LOAD_PREVIOUS_PAGE = 'loadPreviousPage',
  SET_PAGE_NUMBER = 'setPageNumber',
  SET_PAGE_NUMBER_WITHOUT_RELOADING = 'setPageNumberWithoutReloading',
  REFRESH_DATA_FROM_FOOTER = 'refreshDataFromFooter',
  BACK_TO_TOP = 'backToTop',
  SET_PAGE_SIZE = 'setPageSize',
  SELECT_ROWS = 'selectRows',
  EDIT_ROW = 'editRow',
  EDIT_ROW_ENABLED = 'editRowEnabled',
  EDIT_ROW_SAVE = 'editRowSave',
  EDIT_ROW_CANCEL = 'editRowCancel',
  ROWS_LOADING_FINISHED = 'rowsLoadingFinished',
  ROWS_LOADING_STARTED = 'rowsLoadingStarted',
  ROW_CLICKED = 'rowClicked',
  ROW_RIGHT_CLICKED = 'rowRightClicked',
  GRID_SETTINGS_CREATED = 'gridSettingsCreated',
  EXPORT_CSV = 'exportCsv',
  GRID_SORTED = 'gridSorted',
  GRID_SELECT_ALL = 'gridSelectAll',
  COLUMNS_REORDERED = 'columnsReordered',
  AFTER_DISCARD_CHANGES = 'afterDiscardChanges',
}

// Please check the documentation for more details:
// https://bitdefender.atlassian.net/wiki/spaces/GZDOCS/pages/1153469016/The+Grid+Web+Component#Grid-columns-configurable-properties
export enum ColumnType {
  DATE = 0,
  ENUM = 1,
  STRING = 2,
  NUMBER = 3,
  ACTION = 4, // this type of column is for row actions, i.e. context menu, clear button etc.
  BOOL = 5,
  COMPANY = 6,
  SELECT = 7,
  MULTIPLE_VALUE = 8,
  RANGE = 9,
  CUSTOM = 10,
  COMPLEX_SEARCH = 11,
  TAGS = 12,
  COMPLEX_ENUM_FILTER = 13,
  EDITABLE_SELECT = 14,
  CHECKBOX = 15,
  // These 2 constants generate the same filter on the front-end
  REMOTE_SELECT = 16,
  BACKEND_SINGLE_SELECT = 17,
  COMPLIANCE_SELECT = 18,
}

export interface BaseColumnDetails {
  key: string;
  name: string;
  width?: string;
  frozen?: boolean;
  flexGrow?: number;
}

export type RemoteConstantTypes = number | string;

export interface RemoteDetails {
  type: number | string;
  ignoredFieldsForFilters?: Array<string>;
}

// make columns editable by default, add flag to disable inline editing on some columns
export interface ColumnDetails<Column extends number, ColumnKeys extends string> {
  showTimePickers?: boolean;
  column: Column;
  key: ColumnKeys;
  columnName: string;
  type: ColumnType;
  values?: Array<ColumnValue>;
  removable: boolean;
  canCopyContent?: boolean;
  filteringDisabled?: boolean;
  columnIcon?: string;
  // a column is editable by default, setting this flag to TRUE will disable editing for this column
  editingDisabled?: boolean;
  // use this flag to set a custom width for a column
  width?: string;
  filterResetValue?: any;
  category?: string; // setting a category for each column will enable column grouping in the Grid Settings panel
  sortable?: boolean; // setting this flag to TRUE will enable sorting for this column
  required?: boolean;
  resizable?: boolean; // default is TRUE for all columns apart from ACTION type columns
  flexGrow?: number;
  /**
   * Indicates if the can select all option is available for the enum filter component.
   * Defaults to true.
   */
  enumFilterCanSelectAll?: boolean;
  maxSelections?: number;
  exceededLimitError?: string;
  showOnlySelectAll?: boolean;
  showNone?: boolean;
  uiSort?: boolean;
  remoteDetails?: RemoteDetails;
  selectWithDynamicSearch?: boolean;
  isHiddenInGridSettings?: boolean;
  isFilter?: boolean;
}

export interface CompanyColumnDetails<Column extends number, ColumnKeys extends string>
  extends ColumnDetails<Column, ColumnKeys> {
  defaultTarget: Array<string>;
}

export interface ColumnValue {
  label: string;
  value: number | string;
  parent?: string;
}

export interface GetSummaryRequest<ColumnKeys extends string> {
  filters: Array<Filter<ColumnKeys>>;
  extraParams?: any;
  pageSize?: number;
  pageNumber?: number;
  sortColumnName?: string;
  sortDirection?: string;
}

export interface GetSummaryResponse {
  summary: string;
  numItems: number;
  extraDetails?: any;
}

export interface GetRowsRequest<Column extends number, ColumnKeys extends string> {
  columns: Array<Column>;
  filters: Array<Filter<ColumnKeys>>;
  extraParams: any;
  numRecordsToSkip?: number;
  numRecordsToFetch?: number;
  pageSize?: number;
  pageNumber?: number;
  sortColumnName?: string;
  sortDirection?: string;
}

export interface GetRowsResponse {}

export interface UpdateSettingsRequest<Column extends number> {
  viewId: string;
  columns?: Array<Column>;
  compactView?: boolean;
  pageSize?: number;
  pageNumber?: number;
  columnConfigs?: Array<GridColumnConfiguration>;
}

export interface UpdateSettingsResponse {
  columnConfigs?: Array<AlertsColumnConfiguration>;
}

export interface SetFiltersRequest<ColumnKeys extends string> {
  viewId: string;
  filters: Array<Filter<ColumnKeys>>;
}

export interface SetFiltersResponse {}
